import { debug } from './debug';
import { CancelableTask } from './cancelable-task';
const TAG = 'TaskQueue';
/**
 * Task queue is used to run async task sequentially
 *
 * Usage:
 * ```typescript
 * const taskQueue = new TaskQueue();
 *
 *  taskQueue.addExecutor(() => of('Task 1 finished').pipe(delay(100))).subscribe(console.log);
 *  taskQueue.addExecutor(() => of('Task 2 finished').pipe(delay(1))).subscribe(console.log);
 *
 * // Will output
 * Task 1 finished
 * Task 2 finished
 * ```
 */
export class TaskQueue {
    set workingState(v) {
        this._subscription = v;
        // debug(TAG, `state=${this._subscription !== undefined ? 'WORKING' : 'IDLE'}`);
        if (this._subscription === undefined) {
            this.processNext();
            this.currentTask = undefined;
        }
    }
    constructor() {
        this.tasks = [];
    }
    addExecutor(executor) {
        return this.addTask(new CancelableTask(executor));
    }
    addTask(task) {
        this.tasks.push(task);
        // debug(TAG, `addTask... total ${this.tasks.length}`)
        this.processNext();
        return task.toObservable();
    }
    cancelAll(withError) {
        if (this.currentTask) {
            this.currentTask.cancel(withError);
        }
        let task = this.tasks.shift();
        while (task) {
            task.cancel(withError);
            task = this.tasks.shift();
        }
    }
    processNext() {
        // debug(TAG, `Processing next task... total ${this.tasks.length} state=${this._subscription !== undefined ? 'WORKING' : 'IDLE'}`)
        if (this._subscription === undefined) {
            const task = this.tasks.shift();
            if (task) {
                // debug(TAG, `Processing next task... remaining ${this.tasks.length}`)
                this.currentTask = task;
                let isDone = false;
                const observable = task.execute();
                const subscription = observable.subscribe({
                    next: () => {
                        // debug(TAG, `Task done successful!`)
                        this.workingState = undefined;
                        isDone = true;
                    },
                    error: (err) => {
                        debug(TAG, `Task done with error!`, err);
                        this.workingState = undefined;
                        isDone = true;
                    },
                });
                if (!isDone) {
                    this.workingState = subscription;
                }
            }
        }
        else {
            debug(TAG, `addTask... there is already a process running...`);
        }
    }
}
